
import React, { useMemo, useCallback } from "react";
import { isSameDay, format, isBefore } from "date-fns";
import { it } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarEvent } from "@/hooks/calendar/types";
import CalendarTimeSlot from "./CalendarTimeSlot";

interface DayInfo {
  date: Date;
  dayName: string;
  dayShort: string;
  dayNumber: string;
  isToday: boolean;
}

interface CalendarGridProps {
  hours: number[];
  visibleDays: DayInfo[];
  events: CalendarEvent[];
  settings: any;
  onTimeSlotClick: (date: Date, hour: number) => void;
  onDeleteEvent: ((event: CalendarEvent) => Promise<void>) | undefined;
  isTimeSlotUnavailable: (date: Date, hour: number) => boolean;
  isTimeSlotInPast: (date: Date, hour: number) => boolean;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  hours,
  visibleDays,
  events,
  settings,
  onTimeSlotClick,
  onDeleteEvent,
  isTimeSlotUnavailable,
  isTimeSlotInPast
}) => {
  // Memorizziamo questa funzione per evitare calcoli ripetuti ad ogni render
  const getEventsForDateAndHour = useCallback(
    (date: Date, hour: number) => {
      return events.filter(event => isSameDay(event.date, date) && event.hour === hour);
    },
    [events]
  );

  return (
    <div className="border rounded-md overflow-hidden flex-1 flex flex-col">
      {/* Intestazioni dei giorni */}
      <div 
        className="grid border-b" 
        style={{
          gridTemplateColumns: `minmax(50px, auto) repeat(${visibleDays.length}, minmax(80px, 1fr))`
        }}
      >
        <div 
          className="py-2 px-2 sm:px-4 border-r font-medium bg-muted text-center" 
          style={{ width: "60px" }}
        >
          Ora
        </div>
        {visibleDays.map((day, index) => (
          <div 
            key={index} 
            className={`py-2 px-2 sm:px-4 border-r font-medium text-center ${
              day.isToday ? "bg-primary/10" : "bg-muted"
            } ${index === visibleDays.length - 1 ? "border-r-0" : ""}`}
          >
            <div className="hidden sm:block">{day.dayName}</div>
            <div className="sm:hidden text-sm">{day.dayShort}</div>
            <div className={`text-base sm:text-lg ${day.isToday ? "text-primary font-bold" : ""}`}>
              {day.dayNumber}
            </div>
          </div>
        ))}
      </div>

      {/* Griglie delle ore */}
      {hours.length > 0 && (
        <ScrollArea className="flex-1">
          <div className="w-full">
            {hours.map(hour => (
              <div 
                key={hour} 
                className="grid border-b last:border-b-0" 
                style={{
                  gridTemplateColumns: `minmax(50px, auto) repeat(${visibleDays.length}, minmax(80px, 1fr))`
                }}
              >
                <div 
                  className="p-2 border-r font-medium bg-muted text-center" 
                  style={{ width: "60px" }}
                >
                  {hour}:00
                </div>
                {visibleDays.map((day, index) => {
                  const cellEvents = getEventsForDateAndHour(day.date, hour);
                  const isUnavailable = isTimeSlotUnavailable(day.date, hour);
                  const isInPast = isTimeSlotInPast(day.date, hour);
                  
                  return (
                    <CalendarTimeSlot
                      key={index}
                      date={day.date}
                      hour={hour}
                      isToday={day.isToday}
                      isUnavailable={isUnavailable}
                      isInPast={isInPast}
                      events={cellEvents}
                      onTimeSlotClick={onTimeSlotClick}
                      onDeleteEvent={onDeleteEvent}
                      isLastColumn={index === visibleDays.length - 1}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

// Memoizziamo l'intero componente per prevenire renderizzazioni non necessarie
export default React.memo(CalendarGrid);
